import { concatBytes, utf8 } from "./encoding";
import type { V2KeyContext } from "./keys";
import {
  PRIVATE_SESSION_IDLE_MS,
  isPrivateSessionIdleMs,
  requirePrivateSessionIdleMs,
} from "./sessionPolicy";

export { PRIVATE_SESSION_IDLE_MS } from "./sessionPolicy";
const DATABASE = "redacted-v2-session-vault";
const HANDLE = "redacted-v2-session-handle";
const STORE = "sessions";
export type PrivateSessionBinding = V2KeyContext & { providerId: string };
type Miss = { status: "empty" | "expired" | "unavailable" };
export type PrivateSessionRestore =
  | Miss
  | {
      status: "restored";
      rootSeed: Uint8Array;
      keyContext: V2KeyContext;
      lastActivity: number;
      expiresAt: number;
    };
export type PrivateSessionTouch =
  | Miss
  | {
      status: "active";
      lastActivity: number;
      expiresAt: number;
    };
type RecordValue = {
  version: 1;
  key: CryptoKey;
  iv: Uint8Array;
  ciphertext: ArrayBuffer;
  lastActivity: number;
  expiresAt: number;
  observedAt: number;
};
let generation = 0;
let queue: Promise<unknown> = Promise.resolve();
const serial = <T>(run: () => Promise<T>): Promise<T> => {
  const next = queue.then(run, run);
  queue = next.catch(() => undefined);
  return next;
};
const validString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0 && value.length <= 256;
const validTime = (value: number) => Number.isSafeInteger(value) && value > 0;
const token = () =>
  [...crypto.getRandomValues(new Uint8Array(24))]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
const handle = () => {
  const value = sessionStorage.getItem(HANDLE);
  if (value && !/^[0-9a-f]{48}$/.test(value))
    throw new Error("Invalid session");
  return value;
};
const open = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    let blocked = false;
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () =>
      blocked ? request.result.close() : resolve(request.result);
    request.onerror = () => reject(new Error("Session storage unavailable"));
    request.onblocked = () => {
      blocked = true;
      reject(new Error("Session storage blocked"));
    };
  });
const read = async (id: string): Promise<RecordValue | undefined> => {
  const db = await open();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const request = tx.objectStore(STORE).get(id);
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = tx.onabort = () => reject(new Error("Session read failed"));
    });
  } finally {
    db.close();
  }
};
// Opportunistic cleanup includes abandoned tabs. No script can guarantee
// deletion while the browser is stopped; expired keys are removed on next use.
const prune = async (keep: string | null = null) => {
  const db = await open();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const request = tx.objectStore(STORE).openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        const value = cursor.value as RecordValue;
        const now = Date.now();
        if (
          cursor.key !== keep &&
          (!value ||
            value.version !== 1 ||
            !validTime(value.lastActivity) ||
            !validTime(value.observedAt) ||
            !isPrivateSessionIdleMs(value.expiresAt - value.lastActivity) ||
            value.observedAt < value.lastActivity ||
            now < value.observedAt ||
            now >= value.expiresAt)
        )
          cursor.delete();
        cursor.continue();
      };
      tx.oncomplete = () => resolve();
      tx.onerror = tx.onabort = () =>
        reject(new Error("Session cleanup failed"));
    });
  } finally {
    db.close();
  }
};
// Atomic compare-and-swap prevents a copied/stale tab handle from recreating a
// record another tab has consumed, replaced or cleared while crypto was pending.
const replace = async (
  id: string,
  expected: RecordValue | undefined,
  nextId?: string,
  next?: RecordValue,
  current: () => boolean = () => true
): Promise<boolean> => {
  const db = await open();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const request = store.get(id);
      let changed = false;
      request.onsuccess = () => {
        const previous = request.result as RecordValue | undefined;
        if (
          !current() ||
          (expected &&
            (!previous ||
              previous.iv?.toString() !== expected.iv.toString() ||
              !(previous.ciphertext instanceof ArrayBuffer) ||
              new Uint8Array(previous.ciphertext).toString() !==
                new Uint8Array(expected.ciphertext).toString() ||
              previous.observedAt !== expected.observedAt))
        )
          return;
        store.delete(id);
        if (nextId && next) store.put(next, nextId);
        changed = true;
      };
      tx.oncomplete = () => resolve(changed);
      tx.onerror = tx.onabort = () => reject(new Error("Session write failed"));
    });
  } finally {
    db.close();
  }
};

/** Convenience cache, not a backup or an XSS boundary. Same-origin code can
 * use the non-extractable key to decrypt; expiry is an application idle policy,
 * not on-chain revocation. Browsers can copy/restore sessionStorage tab handles. */
export const createPrivateSessionVault = (
  input: PrivateSessionBinding,
  options: { idleTimeoutMs?: number } = {}
) => {
  const idleTimeoutMs = requirePrivateSessionIdleMs(
    options.idleTimeoutMs ?? PRIVATE_SESSION_IDLE_MS
  );
  const binding = { ...input };
  if (
    ![
      binding.chainId,
      binding.proxyAddress,
      binding.walletAddress,
      binding.providerId,
    ].every(validString)
  )
    throw new Error("Invalid private session binding");
  const bound = JSON.stringify([
    binding.chainId,
    binding.proxyAddress,
    binding.walletAddress,
    binding.providerId,
  ]);
  const aad = (
    id: string,
    value: Pick<RecordValue, "lastActivity" | "expiresAt" | "observedAt">
  ) =>
    utf8(
      JSON.stringify([
        DATABASE,
        1,
        id,
        bound,
        value.lastActivity,
        value.expiresAt,
        value.observedAt,
      ])
    );
  const contextValid = (value: V2KeyContext) =>
    value &&
    value.chainId === binding.chainId &&
    value.proxyAddress === binding.proxyAddress &&
    validString(value.walletAddress);
  const expire = async (id: string) => {
    if (handle() === id) sessionStorage.removeItem(HANDLE);
    await replace(id, undefined);
  };
  const encrypt = async (
    id: string,
    bytes: Uint8Array,
    key: CryptoKey,
    lastActivity: number,
    observedAt: number,
    duration: number = idleTimeoutMs
  ): Promise<RecordValue> => {
    const value = {
      version: 1 as const,
      key,
      iv: crypto.getRandomValues(new Uint8Array(12)),
      lastActivity,
      expiresAt: lastActivity + duration,
      observedAt,
    };
    return {
      ...value,
      ciphertext: await crypto.subtle.encrypt(
        { name: "AES-GCM", iv: value.iv, additionalData: aad(id, value) },
        key,
        bytes
      ),
    };
  };
  const inspect = async (id: string, now: number, renew: boolean) => {
    const value = await read(id);
    if (!value) return { status: "empty" as const };
    if (
      value.version !== 1 ||
      !validTime(now) ||
      !validTime(value.lastActivity) ||
      !validTime(value.observedAt) ||
      value.observedAt < value.lastActivity ||
      !isPrivateSessionIdleMs(value.expiresAt - value.lastActivity) ||
      !(value.iv instanceof Uint8Array) ||
      value.iv.length !== 12 ||
      !(value.ciphertext instanceof ArrayBuffer) ||
      value.ciphertext.byteLength > 2048 ||
      value.key?.type !== "secret" ||
      value.key.extractable ||
      value.key.algorithm.name !== "AES-GCM" ||
      (value.key.algorithm as AesKeyAlgorithm).length !== 256
    )
      throw new Error("Invalid session");
    const bytes = new Uint8Array(
      await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: value.iv, additionalData: aad(id, value) },
        value.key,
        value.ciphertext
      )
    );
    try {
      const keyContext = JSON.parse(
        new TextDecoder("utf-8", { fatal: true }).decode(bytes.subarray(32))
      ) as V2KeyContext;
      if (bytes.length < 33 || !contextValid(keyContext))
        throw new Error("Invalid session");
      // Renewal is authorized by captured deliberate activity, not its later
      // processing time. A late batch may extend only a previously live event.
      // Expiry is already authenticated in the original record's AAD. Preserve
      // legacy records and never extend a dormant session merely by selecting
      // a longer policy. Only a deliberate touch can authorize renewal.
      const previousDeadline = Math.min(
        value.expiresAt,
        value.lastActivity + idleTimeoutMs
      );
      const deadline = renew ? now + idleTimeoutMs : previousDeadline;
      if (
        (renew &&
          (now < value.lastActivity ||
            now >= previousDeadline ||
            !validTime(deadline))) ||
        now > Date.now() ||
        Date.now() < value.observedAt ||
        Date.now() >= deadline
      ) {
        bytes.fill(0);
        return { status: "expired" as const };
      }
      return { status: "valid" as const, value, bytes, keyContext };
    } catch (error) {
      bytes.fill(0);
      throw error;
    }
  };
  const resume = (
    renew: boolean,
    now: number
  ): Promise<PrivateSessionRestore | PrivateSessionTouch> => {
    const epoch = generation;
    return serial(async () => {
      let id: string | null = null;
      let writtenId: string | undefined;
      let bytes: Uint8Array | undefined;
      try {
        if (epoch !== generation) return { status: "empty" };
        id = handle();
        await prune(id);
        if (epoch !== generation) return { status: "empty" };
        if (!id) return { status: "empty" };
        const result = await inspect(id, now, renew);
        if (result.status !== "valid") {
          await expire(id);
          return result;
        }
        bytes = result.bytes;
        const nextId = renew ? id : token();
        const value = await encrypt(
          nextId,
          bytes,
          result.value.key,
          renew
            ? Math.max(now, result.value.lastActivity)
            : result.value.lastActivity,
          Math.max(result.value.observedAt, Date.now()),
          renew
            ? idleTimeoutMs
            : Math.min(
                idleTimeoutMs,
                result.value.expiresAt - result.value.lastActivity
              )
        );
        if (epoch !== generation || handle() !== id) return { status: "empty" };
        if (
          Date.now() < value.observedAt ||
          Date.now() < now ||
          Date.now() >= value.expiresAt
        ) {
          await expire(id);
          return { status: "expired" };
        }
        if (
          !(await replace(
            id,
            result.value,
            nextId,
            value,
            () =>
              epoch === generation &&
              Date.now() >= now &&
              Date.now() >= value.observedAt &&
              Date.now() < value.expiresAt
          ))
        )
          return { status: "empty" };
        writtenId = nextId;
        if (epoch !== generation) {
          await replace(nextId, undefined);
          return { status: "empty" };
        }
        if (Date.now() < value.observedAt || Date.now() >= value.expiresAt) {
          await expire(nextId);
          return { status: "expired" };
        }
        sessionStorage.setItem(HANDLE, nextId);
        if (renew)
          return {
            status: "active",
            lastActivity: value.lastActivity,
            expiresAt: value.expiresAt,
          };
        return {
          status: "restored",
          rootSeed: bytes.slice(0, 32),
          keyContext: result.keyContext,
          lastActivity: value.lastActivity,
          expiresAt: value.expiresAt,
        };
      } catch {
        try {
          if (id) await expire(id);
          else sessionStorage.removeItem(HANDLE);
          if (writtenId && writtenId !== id)
            await replace(writtenId, undefined);
        } catch {
          /* Fail closed. */
        }
        return { status: "unavailable" };
      } finally {
        bytes?.fill(0);
      }
    });
  };
  return {
    save(
      rootSeed: Uint8Array,
      keyContext: V2KeyContext,
      now = Date.now()
    ): Promise<boolean> {
      const epoch = generation;
      if (
        rootSeed.length !== 32 ||
        !contextValid(keyContext) ||
        !validTime(now)
      )
        return Promise.resolve(false);
      const bytes = concatBytes(
        rootSeed,
        utf8(
          JSON.stringify({
            chainId: keyContext.chainId,
            proxyAddress: keyContext.proxyAddress,
            walletAddress: keyContext.walletAddress,
          })
        )
      );
      return serial(async () => {
        let id: string | undefined;
        let oldId: string | null = null;
        try {
          if (epoch !== generation) return false;
          oldId = handle();
          await prune();
          if (epoch !== generation) return false;
          id = token();
          const key = await crypto.subtle.generateKey(
            { name: "AES-GCM", length: 256 },
            false,
            ["encrypt", "decrypt"]
          );
          const value = await encrypt(
            id,
            bytes,
            key,
            now,
            Math.max(now, Date.now())
          );
          if (
            epoch !== generation ||
            Date.now() < now ||
            Date.now() >= value.expiresAt
          )
            return false;
          if (
            !(await replace(
              oldId ?? id,
              undefined,
              id,
              value,
              () =>
                epoch === generation &&
                Date.now() >= now &&
                Date.now() < value.expiresAt
            ))
          )
            return false;
          if (epoch !== generation) {
            await replace(id, undefined);
            return false;
          }
          if (Date.now() < value.observedAt || Date.now() >= value.expiresAt) {
            await replace(id, undefined);
            return false;
          }
          sessionStorage.setItem(HANDLE, id);
          return true;
        } catch {
          try {
            sessionStorage.removeItem(HANDLE);
            if (id) await replace(id, undefined);
            if (oldId) await replace(oldId, undefined);
          } catch {
            /* Fail closed. */
          }
          return false;
        } finally {
          bytes.fill(0);
        }
      });
    },
    load: (now = Date.now()) =>
      resume(false, now) as Promise<PrivateSessionRestore>,
    touch: (now = Date.now()) =>
      resume(true, now) as Promise<PrivateSessionTouch>,
    clear(): Promise<void> {
      generation += 1;
      let id: string | null = null;
      try {
        id = handle();
        sessionStorage.removeItem(HANDLE);
      } catch {
        /* No fallback storage. */
      }
      return serial(async () => {
        try {
          if (id) await replace(id, undefined);
        } catch {
          /* Handle already detached. */
        }
      });
    },
  };
};
